import { createSchedulingGridContent } from './createSchedulingContent';
import { CalendarDayList, CalendarDayConfig } from 'owa-calendar-surface-core';
import * as React from 'react';
import {
    AllDayWellBehavior,
    CalendarSurfaceStore,
    getTimeStripWidth,
    TimeStripBehavior,
    TimeStripHeaderBehavior,
    AutoScrollBehavior,
    CalendarSurfaceTransformProps,
    DatesColumnProvider,
    LinearItemsLayout,
} from 'owa-calendar-surface-dategrid';
import { observer } from 'mobx-react-lite';

/**
 * Function to create default scheduling config.
 * @param store the surface store
 */
export function createSchedulingConfig(store: CalendarSurfaceStore): CalendarSurfaceTransformProps {
    return {
        columnProvider: new DatesColumnProvider(store),
        itemsLayout: new LinearItemsLayout(store),
        renderDateHeaderAddOn: null,
        dragDropConfig: {
            canDrag: () => false,
            canDrop: () => false,
            onItemDropped: null,
            onDragStart: null,
            onDragEnd: null,
            getSurfaceItemForPreview: () => {
                return null;
            },
        },
        getItemConfig: null,
    };
}

/**
 * Scheduling grid component
 */
export default observer(function SchedulingGrid({
    store,
    config,
    inDayBehaviors,
    renderInDayBehaviors,
    allDayBehaviors,
}: {
    store: CalendarSurfaceStore;
    config: CalendarSurfaceTransformProps;
    inDayBehaviors?: JSX.Element[];
    renderInDayBehaviors?: JSX.Element[];
    allDayBehaviors?: JSX.Element[];
}) {
    const timeZoneIds = store.timeZoneIds;
    const timeStripWidth = getTimeStripWidth(timeZoneIds);
    const showMultipleTimeZones = timeZoneIds && timeZoneIds.length > 1;

    let schedulingGridConfig: CalendarDayConfig = {
        setFocusedElement: () => null,
        hideTopHeaders: true,
        timeHeaderWidth: showMultipleTimeZones
            ? timeStripWidth * timeZoneIds.length
            : timeStripWidth,
        calendarDayContentConfigProvider: () => {
            return [
                {
                    content: createSchedulingGridContent(store, config),
                    allDayBehaviors: allDayBehaviors || [
                        <AllDayWellBehavior key="weather" store={store} config={config} />,
                    ],
                    inDayBehaviors: inDayBehaviors || [<AutoScrollBehavior key="autoscroll" />],
                    renderInDayBehaviors: renderInDayBehaviors || [],
                    timeStripBehaviors: [
                        <TimeStripBehavior
                            key="timestrip"
                            headerWidth={timeStripWidth}
                            itemsLayout={config.itemsLayout}
                            surfaceStore={store}
                            timeZones={timeZoneIds}
                        />,
                    ],
                    timeStripHeaderBehaviors: [
                        <TimeStripHeaderBehavior
                            key="header"
                            timeZones={showMultipleTimeZones ? timeZoneIds : []}
                            headerWidth={timeStripWidth}
                        />,
                    ],
                },
            ];
        },
    };

    return <CalendarDayList config={schedulingGridConfig} store={store} />;
});
